<template>
  <section class="new-button-set-view">
    <div class="new-button-set-view__header px-4 pt-4 pb-3">
      <CiBreadcrumbs :breadcrumbs="breadcrumbs" class="mb-2" />
      <div style="display: flex; align-items: center;">
        <RdxButton
          rect
          :to="{ name: 'CarsInventoryButtonSets' }"
          class="ci-action--back"
        >
          <RdxIcon class="ri-arrow-left-line rdx-icon--24" />
        </RdxButton>
        <CiHeading level="1" class="ml-2">
          {{ button_set_init.name || "..." }}
        </CiHeading>
        <div class="new-button-set-view__actions">
          <RdxButton
            outline
            class="mr-2"
            data-cy="cancel"
            :disabled="!isDirty || pending"
            @click="cancel"
          >
            {{ $t("global.cancel") }}
          </RdxButton>
          <RdxButton
            filled
            :disabled="!(isDirty && isValid) || pending"
            @click="updateButtonSet"
          >
            {{ $t("global.save") }}
          </RdxButton>
        </div>
      </div>
    </div>

    <div class="new-button-set-view__inner">
      <div class="p-5">
        <div class="row new-button-set-view__grid">
          <div>
            <CiHeading level="2" class="mb-2">
              {{ $t("button-sets.settings") }}
            </CiHeading>
            <RdxInfo>
              {{ $t("button-sets.settings-description") }}
            </RdxInfo>
          </div>
          <div class="col-xl-9">
            <RdxInput
              v-model="button_set.name"
              :label="$t('button-sets.form.button-set-name-input-label')"
              :placeholder="
                $t('button-sets.form.button-set-name-input-placeholder')
              "
              class="mb-5"
            />
            <CiButtonSetWrapper>
              <template #header>
                <CiHeading level="2" class="mb-2">
                  {{ $t("button-sets.form.primary-button") }}
                </CiHeading>
              </template>
              <template #description>
                <RdxInfo>
                  {{ $t("button-sets.form.primary-button-description") }}
                </RdxInfo>
              </template>
            </CiButtonSetWrapper>
            <CiButtonSetEditForm
              v-if="primaryButton"
              :value="primaryButton"
              type="primary"
              :label-placeholder="
                $t('button-sets.form.label-input-placeholder-primary')
              "
              @input="updatePrimary"
            />
            <RdxDivider class="mb-5" />

            <CiButtonSetWrapper>
              <template #header>
                <CiHeading level="2" class="mb-2">
                  {{ $t("button-sets.form.secondary-button") }}
                </CiHeading>
              </template>
              <template #description>
                <RdxInfo>
                  {{ $t("button-sets.form.secondary-button-description") }}
                </RdxInfo>
              </template>
              <template #action>
                <RdxButton
                  v-if="!secondaryButtons.length"
                  outline
                  @click="addSecondary"
                >
                  {{ $t("button-sets.form.add-button") }}
                </RdxButton>
              </template>
            </CiButtonSetWrapper>
            <div v-if="secondaryButtons.length !== 0">
              <RdxGroupTabs
                :groups="secondaryButtons"
                :current="currentTabIndex"
                :tab-label="$t('button-sets.form.tab-label')"
                :tooltip-content="$t('button-sets.form.add-button-tooltip')"
                :max="4"
                @add="addSecondary"
                @current="changeTab"
                @remove="removeSecondary"
              />
              <CiButtonSetEditForm
                v-for="(secondaryButton, index) of secondaryButtons"
                v-show="currentTabIndex === index"
                :key="`button--${index}`"
                :value="secondaryButton"
                type="secondary"
                :label-placeholder="
                  $t('button-sets.form.label-input-placeholder-secondary')
                "
                @input="updateSecondary($event, index)"
              />
            </div>
            <div class="mt-4">
              <RdxButton variant="red" filled @click="openButtonSetRemoveModal">
                {{ $t("button-sets.delete-set") }}
              </RdxButton>
              <RdxInfo icon="ri-information-line">
                {{ $t("button-sets.delete-set-warning-message") }}
              </RdxInfo>
            </div>
          </div>
        </div>
      </div>
    </div>
    <CiWarningModal
      v-if="warningModalOpen"
      :heading="$t('global.skip-editing-modal.header')"
      :paragraph="$t('global.skip-editing-modal.description')"
      @confirm="leavePage"
      @cancel="warningModalOpen = $event"
    >
      <template #confirm>
        <RdxButton filled class="ci-warning-modal__button" @click="leavePage">
          {{ $t("global.skip-editing-modal.confirm-button") }}
        </RdxButton>
      </template>
    </CiWarningModal>
    <CiDeleteModal
      v-if="deleteModal.open"
      :heading="deleteModal.header"
      :paragraph="$t('button-sets.remove-secondary-modal.description')"
      :delete-text="$t('button-sets.remove-modal.confirm-button')"
      :cancel-text="$t('global.cancel')"
      @delete="removeSecondaryByModal"
      @cancel="closeDeleteModal"
    />
    <CiDeleteModal
      v-if="removeButtonSetModal"
      :heading="$t('button-sets.remove-modal.header')"
      :paragraph="$t('button-sets.remove-modal.description')"
      :delete-text="$t('button-sets.remove-modal.confirm-button')"
      :cancel-text="$t('global.cancel')"
      @delete="removeButtonSet"
      @cancel="removeButtonSetModal = false"
    />
    <CiWarningModal
      v-if="blockRemoveButtonSetModal"
      :heading="$t('button-sets.block-remove-modal.header')"
      @cancel="blockRemoveButtonSetModal = $event"
    >
      {{ $t("button-sets.block-remove-modal.description") }}
      <li v-for="list of includedLists" :key="`publication-list-${list.id}`">
        <router-link
          :to="{
            name: 'CarsInventoryEditList',
            params: { id: list.id }
          }"
          target="_blank"
        >
          {{ list.attributes.title }}
        </router-link>
      </li>
      {{ $t("button-sets.block-remove-modal.tip") }}
      <template #confirm><span></span></template>
    </CiWarningModal>
  </section>
</template>

<script>
import {
  RdxButton,
  RdxIcon,
  RdxInput,
  RdxInfo,
  RdxDivider,
  RdxGroupTabs
} from "@raffine/rdx-ui/lib/rdx-ui.umd";

import CiBreadcrumbs from "@/components/atoms/CiBreadcrumbs.vue";
import CiHeading from "@/components/atoms/CiHeading";
import CiButtonSetWrapper from "@/components/atoms/CiButtonSetWrapper";
import CiButtonSetEditForm from "@/components/molecules/CiButtonSetEditForm";
import CiWarningModal from "@/components/CiWarningModal/CiWarningModal";
import CiDeleteModal from "@/components/CiDeleteModal/CiDeleteModal";
import { isValid } from "./buttoSetValidation";

export default {
  name: "EditButtonSet",

  components: {
    RdxButton,
    RdxIcon,
    RdxInput,
    RdxInfo,
    RdxDivider,
    RdxGroupTabs,

    CiHeading,
    CiBreadcrumbs,
    CiButtonSetWrapper,
    CiButtonSetEditForm,
    CiWarningModal,
    CiDeleteModal
  },

  data() {
    return {
      currentTabIndex: 0,
      button_set_init: {
        label: null,
        buttons: [
          {
            label: null,
            target_type: "url",
            button_type: "primary",
            target: null,
            target_action: "new_tab"
          }
        ]
      },

      button_set: {
        label: null,
        buttons: []
      },
      secondaryInitState: {
        label: null,
        target_type: "url",
        button_type: "secondary",
        target: null,
        target_action: "new_tab"
      },

      warningModalOpen: false,
      pending: false,
      deleteModal: {
        open: false,
        header: null,
        toRemove: null
      },

      removeButtonSetModal: false,
      blockRemoveButtonSetModal: false,
      hasAnyListingRelation: false,
      includedLists: []
    };
  },

  computed: {
    breadcrumbs() {
      return [
        { text: "CarsInventory", link: { name: "CarsInventoryStock" } },
        {
          text: this.$t("navbar.buttons"),
          link: { name: "CarsInventoryButtonSets" }
        },
        { text: this.button_set_init.name || "..." }
      ];
    },

    ID() {
      const { id } = this.$route.params;
      return id;
    },
    primaryButton() {
      return this.button_set.buttons.find(el => el.button_type === "primary");
    },
    secondaryButtons() {
      return this.button_set.buttons.filter(
        el => el.button_type === "secondary"
      );
    },

    isDirty() {
      return (
        this.converToString(this.button_set) !==
        this.converToString(this.button_set_init)
      );
    },

    isValid
  },

  watch: {
    secondaryButtons: {
      deep: true,
      handler(newList) {
        let listLength = newList.length;
        let maxIndex = listLength - 1;
        if (!listLength || this.currentTabIndex > maxIndex)
          this.currentTabIndex = 0;
      }
    }
  },

  mounted() {
    this.pending = true;
    this.$store
      .dispatch("buttonSet/fetchById", this.ID)
      .then(({ data: { data, included } }) => {
        this.init_data(data, included);
        this.pending = false;
      });
  },

  methods: {
    updatePrimary(value) {
      let index = this.button_set.buttons.indexOf(this.primaryButton);
      this.$set(this.button_set.buttons, index, value);
    },
    updateSecondary(value, index) {
      this.$set(this.button_set.buttons, index + 1, value);
    },
    addSecondary() {
      this.button_set.buttons.push(this.secondaryInitState);
      this.currentTabIndex = this.secondaryButtons.length - 1;
    },
    changeTab(index) {
      this.currentTabIndex = index;
    },
    removeSecondary({ index, group }) {
      if (!group.id) {
        this.button_set.buttons.splice(index + 1, 1);
      } else {
        this.deleteModal.toRemove = index + 1;
        this.deleteModal.header = this.$t(
          "button-sets.remove-secondary-modal.header",
          { index: index + 1 }
        );
        this.deleteModal.open = true;
      }
    },

    closeDeleteModal() {
      this.deleteModal.toRemove = null;
      this.deleteModal.header = null;
      this.deleteModal.open = false;
    },

    removeSecondaryByModal() {
      this.button_set.buttons.splice(this.deleteModal.toRemove, 1);
      this.closeDeleteModal();
    },

    async updateButtonSet() {
      this.pending = true;
      try {
        let {
          data: { data, included }
        } = await this.$store.dispatch("buttonSet/update", {
          id: this.ID,
          payload: {
            data: {
              attributes: this.button_set
            }
          }
        });

        this.$toast.info(this.$t("button-sets.button-set-updated-successfull"));
        this.init_data(data, included);
      } finally {
        this.pending = false;
      }
    },

    converToString(object) {
      return JSON.stringify(object);
    },
    cloneObject(object) {
      return JSON.parse(this.converToString(object));
    },

    cancel() {
      if (this.isDirty) {
        this.warningModalOpen = true;
      } else {
        this.leavePage();
      }
    },

    leavePage() {
      this.$router.push({
        name: "CarsInventoryButtonSets"
      });
    },

    openButtonSetRemoveModal() {
      if (this.hasAnyListingRelation) {
        this.blockRemoveButtonSetModal = true;
      } else {
        this.removeButtonSetModal = true;
      }
    },

    removeButtonSet() {
      this.$store.dispatch("buttonSet/delete", this.ID).then(() => {
        this.leavePage();
        this.$toast.info(this.$t("button-sets.button-set-removed-successfull"));
      });
    },

    init_data(data, included) {
      const buttons = included.filter(({ type }) => type === "button");
      this.includedLists = included.filter(({ type }) => type === "listing");
      let init_data = {
        name: data.attributes.name,
        buttons: buttons.map(el => ({ id: el.id, ...el.attributes }))
      };
      this.button_set_init = this.cloneObject(init_data);
      this.button_set = this.cloneObject(init_data);
      if (
        data.relationships.listings &&
        data.relationships.listings.data.length
      ) {
        this.hasAnyListingRelation = true;
      }
    }
  }
};
</script>

<style lang="scss">
.new-button-set-view {
  &__header {
    border-bottom: 1px solid $grey-5;
    padding: 17px 24px 21px;
  }

  &__inner {
    width: 100%;
    height: 100%;
    max-height: calc(100vh - 186px);
    overflow: auto;
  }

  &__actions {
    margin-left: auto;
  }

  &__grid {
    display: grid;
    grid-template-columns: 25% 75%;
  }

  .rdx-group-tab {
    background-color: #fff;

    &--current {
      background-color: #fff;
      &:after {
        border-color: #fff;
      }
    }
  }
}
</style>
